import React, { useState } from 'react';
import axios from 'axios';
import { Users, Crown } from 'lucide-react';
import { getBackendURL } from '../../utils/backendURL';
import { showSuccess, showError, showWarning } from '../../utils/opsNotifications';
import BusinessTrialControl from './BusinessTrialControl';
import UserTeamLimitDialogs from './UserTeamLimitDialogs';

const OpsSubscriptionSettings = ({ user, plans, onRefresh }) => {
  const [userDetails, setUserDetails] = useState(user);
  const [updating, setUpdating] = useState(false);
  const [showTeamLimitConfirm, setShowTeamLimitConfirm] = useState(false);
  const [showRemoveTeamLimitConfirm, setShowRemoveTeamLimitConfirm] = useState(false);
  const [pendingTeamLimit, setPendingTeamLimit] = useState(null);

  const [showTeamLimitInput, setShowTeamLimitInput] = useState(false);
  const [customTeamLimit, setCustomTeamLimit] = useState('');

  const handlePlanChange = async (newPlan) => {
    try {
      setUpdating(true);
      const token = localStorage.getItem('admin_token');
      const backendUrl = getBackendURL();

      console.log('Updating plan:', { userId: user.id, newPlan, backendUrl });

      const response = await axios.put(`${backendUrl}/api/ops/users/${user.id}/plan`, {
        plan: newPlan
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Plan update response:', response.data);

      setUserDetails({ ...userDetails, plan: newPlan });
      await onRefresh(); // Refresh the main list
      showSuccess(`Plan updated to ${newPlan} successfully`);
    } catch (error) {
      console.error('Failed to update user plan:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update user plan';
      showError(`Failed to update plan: ${errorMsg}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleSetCustomTeamLimit = async () => {
    const limit = parseInt(customTeamLimit);
    if (isNaN(limit) || limit < 1) {
      showWarning('Please enter a valid team member limit (minimum 1)');
      return;
    }

    setPendingTeamLimit(limit);
    setShowTeamLimitConfirm(true);
  };

  const confirmSetTeamLimit = async () => {
    setShowTeamLimitConfirm(false);
    const limit = pendingTeamLimit;

    try {
      setUpdating(true);
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${getBackendURL()}/api/ops/users/${userDetails.id}/team-limit`,
        { team_members_limit: limit },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setUserDetails(previous => ({ ...previous, custom_team_members_limit: limit }));
      showSuccess(`✓ Custom team limit set to ${limit} members`);
      setShowTeamLimitInput(false);
      setCustomTeamLimit('');
      await onRefresh();
    } catch (error) {
      console.error('Failed to set custom team limit:', error);
      showError(`Failed to set custom team limit: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveCustomTeamLimit = async () => {
    setShowRemoveTeamLimitConfirm(true);
  };

  const confirmRemoveTeamLimit = async () => {
    setShowRemoveTeamLimitConfirm(false);

    try {
      setUpdating(true);
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${getBackendURL()}/api/ops/users/${userDetails.id}/team-limit`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setUserDetails(previous => ({ ...previous, custom_team_members_limit: null }));
      showSuccess('✓ Custom team limit removed, reverted to trial or plan limit');
      setShowTeamLimitInput(false);
      await onRefresh();
    } catch (error) {
      console.error('Failed to remove custom team limit:', error);
      showError(`Failed to remove custom team limit: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  return <>
          {/* Plan Management */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Plan</h3>

            <BusinessTrialControl user={userDetails} disabled={updating}
              onUpdated={async (updated) => { setUserDetails(updated); await onRefresh(); }} />

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {plans.map((plan) => (
                <button
                  key={plan}
                  onClick={() => handlePlanChange(plan)}
                  disabled={updating || plan === userDetails.plan}
                  className={`p-3 text-center rounded-lg border-2 transition-all ${
                    plan === userDetails.plan
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    plan === userDetails.plan ? 'bg-blue-500 text-white' : 'bg-gray-100'
                  }`}>
                    <Crown className="w-4 h-4" />
                  </div>
                  <p className="text-xs font-medium capitalize">{plan}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Team Member Limit */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2 text-indigo-600" />
              Custom Team Member Limit
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Override the plan's default team member limit for this specific user. Useful for custom agreements or special cases.
            </p>

            {!showTeamLimitInput ? (
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowTeamLimitInput(true)}
                  disabled={updating}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  Set Custom Limit
                </button>
                <button
                  onClick={handleRemoveCustomTeamLimit}
                  disabled={updating}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Reset to Default
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex space-x-3">
                  <input
                    type="number"
                    min="1"
                    placeholder="Enter team member limit (e.g., 12)"
                    value={customTeamLimit}
                    onChange={(e) => setCustomTeamLimit(e.target.value)}
                    disabled={updating}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                  />
                  <button
                    onClick={handleSetCustomTeamLimit}
                    disabled={updating || !customTeamLimit}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {updating ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setShowTeamLimitInput(false);
                      setCustomTeamLimit('');
                    }}
                    disabled={updating}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Example: User on Starter plan (10 members) needs 12 members - set custom limit to 12
                </p>
              </div>
            )}
          </div>

    <UserTeamLimitDialogs userDetails={userDetails} pendingTeamLimit={pendingTeamLimit}
      showTeamLimitConfirm={showTeamLimitConfirm} showRemoveTeamLimitConfirm={showRemoveTeamLimitConfirm}
      setShowTeamLimitConfirm={setShowTeamLimitConfirm} setShowRemoveTeamLimitConfirm={setShowRemoveTeamLimitConfirm}
      confirmSetTeamLimit={confirmSetTeamLimit} confirmRemoveTeamLimit={confirmRemoveTeamLimit} />
  </>;
};

export default OpsSubscriptionSettings;
